import { IonApp, IonContent, IonRouterOutlet, IonSplitPane, setupIonicReact } from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';
import { Redirect, Route } from 'react-router-dom';
import Menu from './features/menu/components/Menu';

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';

/* Theme variables */
import './theme/variables.css';
import './theme/layoutClasses.css';
import './theme/textClasses.css';
import './theme/spacingClasses.css';
import './theme/interactionClasses.css';
import './theme/globalOverrides.css';
import PeopleListPage from './pages/PeopleListPage';
import AddPeoplePage from './pages/AddPeoplePage';
import UsageMetricsPage from './pages/UsageMetricsPage';
import * as wjcCore from '@grapecity/wijmo';
import '@grapecity/wijmo.styles/wijmo.css';

import { Amplify } from 'aws-amplify';
import awsconfig from './aws-exports';
import CheckoutCancelPage from './pages/checkout/CheckoutCancelPage';
import { atom, selector } from 'recoil';
import useFetchGovernors, { governorsState } from './features/usage/hooks/useFetchGovernors';
import Toolbar from './features/toolbar/Toolbar';
import OnboardingInfo from './features/login/OnboardingInfo';
import useFetchOrganization from './features/organization/useFetchOrganization';
import { AgreementStatus } from './constants/constants';
import Agreement from './features/legal/Agreement';
import ViewListsPage from './pages/ViewListsPage';
import ListImportPage from './pages/ListImportPage';
import SettingsPage from './pages/SettingsPage';
import IntegrationsPage from './pages/integrations/qm/IntegrationsPage';
import OptOutPage from './pages/OptOutPage';
import useFetchIntegration from './features/integrations/qm/useFetchIntegration';
import TimeTrackingPage from './pages/TimeTrackingPage';
import { AuthCache, PrivateRoute, UserGroups } from '@otuvy/auth';
import Unauthorized from './features/login/Unauthorized';

Amplify.configure(awsconfig);

setupIonicReact();

export const writeAccessRestrictedState = selector<boolean>({
  key: 'writeAccessRestricted',
  get: ({ get }) => {
    const { isWriteAccessRestricted } = get(governorsState) ?? {};
    return !!isWriteAccessRestricted;
  },
});

export const agreementState = atom<AgreementStatus[]>({
  key: 'agreementState',
  default: [],
});

const App: React.FC = () => {
  useFetchOrganization();
  useFetchGovernors();
  useFetchIntegration();

  wjcCore.setLicenseKey(
    'admin.veritaskapp.com.s3-website-us-west-2.amazonaws.com|admin.veritask.io|admin2.veritaskapp.com|admin2.veritaskapp.com.s3-website-us-east-1.amazonaws.com|localhost|devapp.veritask.io|devadmin.veritask.io|app.veritask.io.s3-website-us-west-2.amazonaws.com|admin.veritask.io.s3-website-us-west-2.amazonaws.com|app.veritask.io|admin.veritaskapp.com|devadmin.otuvyfrontline.com|dev.otuvyfrontline.com|admin.otuvyfrontline.com|otuvyfrontline.com|devadmin.otuvyfrontline.com.s3-website-us-west-2.amazonaws.com|dev.otuvyfrontline.com.s3-website-us-west-2.amazonaws.com|otuvyfrontline.com.s3-website-us-east-1.amazonaws.com|admin.otuvyfrontline.com.s3-website-us-east-1.amazonaws.com,379636618582852#B0PvBXYtFmLy4CdzV6dtMXdtUGdpNnYldXLzMnLvlmLrNXY4lmclZnLwBXYs2Waus6chRXayVmdu8WatRWY6VGZs2Waus6chRXayVmduAHchZXZkxCdz3GasF6YvxGLt36YuM7dh96b0FWbh9SMtQ7chVWLzVXLlRXazJWZ75yMz9SbvNmLwBXYrNXY4lmclZnLy8WatRWYs46bj9CcwF6azFGdpJXZ69iMulWbkFGLvlmLrNXY4lmclZnLulWbkFGLt36YuM7dh96b0FWbh9iMtQ7cldXLzVXLlRXazJWZ75yMz9SbvNmLwBXYrNXY4lmclZnLulWbkFmI0IyctRkIsIyazFGVpJXZWJiOiEmTDJCLiITN8IDO5gTM6YzM6kzNzIiOiQWSiwSfdtlOicGbmJCLiIjdyIDMyIiOiIXZ6JCLlNHbhZmOiI7ckJye0ICbuFkI1pjIEJCLi4TP7NkQiFzURNjUwMmZMBVOnRkNUlXbNJjYzx6av9Ec5dXV4V6QMFUbJJ6bONjTMhTdwcFRU3mZKF6RMdjMmpEdrd5R7F7Kkd6L4YGUlZjRLNHOmhjUzomc0VlWvRmbEJ4UYVVdwlEb9ZVejd7ahFkdFl6ZYFlewQDT7NjMudkQ5ADeG96TUx6Zkdld8c4QRBXWmdjbQVDTvFzLXRmd9YmQNVnauVUc9FEN5pVYixUM8JUY5cnZyUXWa36T8x6UKFWSClDVzIjW4NHaWpkQBJ6VE96SqR4c6JHepVkQYxWRxsmNvkjN7NVOPNnZZBnczEkTCFHam3EZqR6QulWVvMHNyI7bWZVNPhFe486TGlDZZB7Qwp4dWpHRWNnbv9mRqBHaq94MUl5ZKplRPFUREZleXNVTO3kU6Bndj3ETjN4QaVXVwsSUNFjTsdUVwkzLCJ7du34NOhlWiojITJCLiMzQEVDMxIENiojIIJCL4ATM5ITO8UTO0IicfJye&Qf35VfikEMyIlI0IyQiwiIu3Waz9WZ4hXRgACdlVGaThXZsZEIv5mapdlI0IiTisHL3JSNJ9UUiojIDJCLi86bpNnblRHeFBCIyV6dllmV4J7bwVmUg2Wbql6ViojIOJyes4nILdDOIJiOiMkIsIibvl6cuVGd8VEIgc7bSlGdsVXTg2Wbql6ViojIOJyes4nI4YkNEJiOiMkIsIibvl6cuVGd8VEIgAVQM3EIg2Wbql6ViojIOJyes4nIzMEMCJiOiMkIsISZy36Qg2Wbql6ViojIOJyes4nIVhzNBJiOiMkIsIibvl6cuVGd8VEIgQnchh6QsFWaj9WYulmRg2Wbql6ViojIOJyebpjIkJHUiwiI8ITMwMDMggTM8AzMyAjMiojI4J7QiwiIt36YuM7dh96b0FWbh9SMtQ7chVWLzVXLlRXazJWZ75yMz9SbvNmLl9WasRnbvJnZ9ZXd43mLulWbkFGLt36YuM7dh96b0FWbh9SMtQ7chVWLzVXLlRXazJWZ75yMz9SbvNmLl9WasRnbvJnZ9ZXd43GLt36YuM7dh96b0FWbh9iMtQ7cldXLzVXLlRXazJWZ75yMz9SbvNmLl9WasRnbvJnZ9ZXd43mL6VGZs46bj9yc7FmbvpXYtFmLy4CdzV6dtMXdtUGdpNnYldXLzMnLt36YuUmbpxGdu3mcmlnd5R7bu8WatRWY6VGZs46bj9SZulGb496byZWe6VHdvxSbvNmLl9WasRnbvJnZ9ZXd43mLulWbkFGLt36YuUmbpxGdu3mcmlnd5R7buYXZkxSbvNmLl9WasRnbvJnZ9ZXd43mLulWbkFmdlRGLt36YuAHcht6chRXayVmdu8WatRWYs2Waus6chRXayVmduAHchxSbvNmLzdXYu3meh5WYuITL4NXZ75yc55SZ4l6ciV6dtMzcu2Waus6chRXayVmdu8WatRWYs46bj9yf5Cm'
  );

  return (
    <IonApp>
      <Toolbar />

      <IonContent>
        <IonReactRouter>
          <IonSplitPane when="md" contentId="main">
            <Menu contentId="main" />

            <IonRouterOutlet id="main">
              <Route path="/" exact={true}>
                {AuthCache.isCurrentUserInAnyGroup([UserGroups.PEOPLE_MANAGER, UserGroups.SUPER_ADMIN]) ? (
                  <Redirect to="/people/list" />
                ) : AuthCache.isCurrentUserInAnyGroup([UserGroups.LIST_MANAGER]) ? (
                  <Redirect to="/lists/view" />
                ) : AuthCache.isCurrentUserInAnyGroup([UserGroups.TIME_TRACKING]) ? (
                  <Redirect to="/timeTracking" />
                ) : AuthCache.isCurrentUserInAnyGroup([UserGroups.QM_INTEGRATION_MANAGER]) ? (
                  <Redirect to="/linkQM" />
                ) : (
                  <Redirect to="/unauthorized" />
                )}
              </Route>
              <PrivateRoute
                path="/people/list"
                exact={true}
                component={PeopleListPage}
                anyGroup={[UserGroups.PEOPLE_MANAGER, UserGroups.SUPER_ADMIN]}
              />
              <PrivateRoute
                path="/people/add"
                exact={true}
                component={AddPeoplePage}
                anyGroup={[UserGroups.PEOPLE_MANAGER, UserGroups.SUPER_ADMIN]}
              />
              <Route path="/lists" exact={true}>
                {AuthCache.isCurrentUserInAnyGroup([UserGroups.LIST_MANAGER]) ? <Redirect to="/lists/view" /> : ''}
              </Route>
              <PrivateRoute
                path="/lists/view"
                exact={true}
                component={ViewListsPage}
                anyGroup={[UserGroups.LIST_MANAGER, UserGroups.SUPER_ADMIN]}
              />
              <PrivateRoute
                path="/lists/import"
                exact={true}
                component={ListImportPage}
                anyGroup={[UserGroups.LIST_MANAGER, UserGroups.SUPER_ADMIN]}
              />
              <PrivateRoute
                path="/timeTracking"
                exact={true}
                component={TimeTrackingPage}
                anyGroup={[UserGroups.TIME_TRACKING, UserGroups.SUPER_ADMIN]}
              />
              <PrivateRoute
                path="/usage"
                exact={true}
                component={UsageMetricsPage}
                anyGroup={[UserGroups.SUPER_ADMIN]}
              />
              <PrivateRoute
                path="/settings"
                component={SettingsPage}
                exact={true}
                anyGroup={[UserGroups.SUPER_ADMIN]}
              />
              <Route path="/linkQM" component={IntegrationsPage} />
              {/* 
                This route is for easily accessing the registration page during development 
                TODO: remove it.  Possibly limit it to a tech support/dev role?
              */}
              <Route path="/register" exact={true} component={OnboardingInfo} />
              <Route path="/checkout/cancel" exact={true} component={CheckoutCancelPage} />
              <Route path="/optOut" component={OptOutPage}></Route>
              <Route path="/unauthorized" exact={true} component={Unauthorized}></Route>
            </IonRouterOutlet>
          </IonSplitPane>
        </IonReactRouter>
        <Agreement />
      </IonContent>
    </IonApp>
  );
};
export default App;
